import type {
  ApprovalLimit,
  CalculationStep,
  ClaimPositionFields,
  CoverageTerm,
  Deckung,
  Deductible,
  GeneralSetting,
  TariffTerm,
  TariffVariation,
  WaitingPeriod,
} from './deckung';

/**
 * A GROUP TARIF's `tarif_deckung` (SDD-018 §6, ui-spec §5): an override per SECTION of the child's
 * document. An absent key is inherited at read — never copied — so "geerbt" is the absence of the
 * section here, and "überschrieben" is its presence, pre-filled with the child's value.
 *
 * `resolveGroupDeckung` mirrors the service's `ResolveTarifDeckung` (domain/config/tarifdeckung.go)
 * so the editor can show the merged result while the override is still a draft; the published
 * resolution is always the service's (`GET /v1/tarif-deckung:resolve`).
 */

export interface VariantOverride {
  readonly coverageTerm?: TariffTerm;
  readonly categories?: Readonly<Record<string, { readonly coverageTerm: CoverageTerm }>>;
  readonly deductibles?: readonly Deductible[];
  readonly waitingPeriod?: WaitingPeriod;
  readonly approvalLimits?: readonly ApprovalLimit[];
  readonly calculationOrder?: { readonly steps: readonly CalculationStep[] };
}

export interface GroupDeckung {
  readonly profile: 'GROUP_TARIF';
  readonly allowedVariants?: readonly string[];
  readonly defaultVariant?: string;
  readonly variantOverrides?: Readonly<Record<string, VariantOverride>>;
  readonly claimConfig?: {
    readonly generalSetting?: GeneralSetting;
    readonly claimPositionFields?: ClaimPositionFields;
  };
}

/** The sections a variant override may replace whole (ui-spec §5). */
export type VariantSection = 'coverageTerm' | 'deductibles' | 'waitingPeriod' | 'approvalLimits' | 'calculationOrder';

/** Every overridable section, addressed. */
export type Section =
  | { readonly kind: 'allowedVariants' }
  | { readonly kind: 'defaultVariant' }
  | { readonly kind: 'variant'; readonly variant: string; readonly section: VariantSection }
  | { readonly kind: 'category'; readonly variant: string; readonly category: string }
  | { readonly kind: 'claimConfig'; readonly section: 'generalSetting' | 'claimPositionFields' };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The typed view of a stored GROUP_TARIF document (an empty draft is `{profile}`). */
export function groupDeckungOf(stored: unknown): GroupDeckung {
  return { ...(isObject(stored) ? stored : {}), profile: 'GROUP_TARIF' };
}

/** The source key the service reports for a section (`GetResolvedTarifDeckung` `sources`). */
export function sourceKeyOf(section: Section): string {
  switch (section.kind) {
    case 'allowedVariants':
    case 'defaultVariant':
      return section.kind;
    case 'variant':
      return `tariffVariations.${section.variant}.${section.section}`;
    case 'category':
      return `tariffVariations.${section.variant}.categories.${section.category}`;
    case 'claimConfig':
      return `claimConfig.${section.section}`;
  }
}

export function isOverridden(group: GroupDeckung, section: Section): boolean {
  switch (section.kind) {
    case 'allowedVariants':
      return group.allowedVariants !== undefined;
    case 'defaultVariant':
      return group.defaultVariant !== undefined;
    case 'variant':
      return group.variantOverrides?.[section.variant]?.[section.section] !== undefined;
    case 'category':
      return group.variantOverrides?.[section.variant]?.categories?.[section.category] !== undefined;
    case 'claimConfig':
      return group.claimConfig?.[section.section] !== undefined;
  }
}

/** The child's value of a section — what "geerbt" shows and what "überschrieben" starts from. */
export function childValueOf(child: Deckung, section: Section): unknown {
  switch (section.kind) {
    case 'allowedVariants':
      return child.tariffVariations.map((v) => v.key);
    case 'defaultVariant':
      return undefined;
    case 'variant': {
      const variant = child.tariffVariations.find((v) => v.key === section.variant);
      return variant?.[section.section];
    }
    case 'category': {
      const variant = child.tariffVariations.find((v) => v.key === section.variant);
      return variant?.categories.find((c) => c.key === section.category)?.coverageTerm;
    }
    case 'claimConfig':
      return child.claimConfig[section.section];
  }
}

function withVariantOverride(group: GroupDeckung, variant: string, change: (o: VariantOverride) => VariantOverride | undefined): GroupDeckung {
  const current = group.variantOverrides?.[variant] ?? {};
  const next = change(current);
  const others = Object.fromEntries(Object.entries(group.variantOverrides ?? {}).filter(([key]) => key !== variant));
  const overrides = next === undefined || Object.keys(next).length === 0 ? others : { ...others, [variant]: next };
  const { variantOverrides: _drop, ...rest } = group;
  return Object.keys(overrides).length === 0 ? (rest) : { ...rest, variantOverrides: overrides };
}

const without = <T extends object, K extends keyof T>(value: T, key: K): Omit<T, K> => {
  const { [key]: _drop, ...rest } = value;
  return rest;
};

/** Sets a section's value — overriding it. */
export function setSection(group: GroupDeckung, section: Section, value: unknown): GroupDeckung {
  switch (section.kind) {
    case 'allowedVariants':
      return { ...group, allowedVariants: value as readonly string[] };
    case 'defaultVariant':
      return { ...group, defaultVariant: value as string };
    case 'variant':
      return withVariantOverride(group, section.variant, (o) => ({ ...o, [section.section]: value }));
    case 'category':
      return withVariantOverride(group, section.variant, (o) => ({
        ...o,
        categories: { ...(o.categories ?? {}), [section.category]: { coverageTerm: value as CoverageTerm } },
      }));
    case 'claimConfig':
      return { ...group, claimConfig: { ...(group.claimConfig ?? {}), [section.section]: value } };
  }
}

/** Removes a section — inheriting it again. */
export function clearSection(group: GroupDeckung, section: Section): GroupDeckung {
  switch (section.kind) {
    case 'allowedVariants':
      return without(group, 'allowedVariants');
    case 'defaultVariant':
      return without(group, 'defaultVariant');
    case 'variant':
      return withVariantOverride(group, section.variant, (o) => without(o, section.section));
    case 'category':
      return withVariantOverride(group, section.variant, (o) => {
        const categories = without({ ...(o.categories ?? {}) }, section.category);
        return Object.keys(categories).length === 0 ? without(o, 'categories') : { ...o, categories };
      });
    case 'claimConfig': {
      const claimConfig = without({ ...(group.claimConfig ?? {}) }, section.section);
      return Object.keys(claimConfig).length === 0 ? (without(group, 'claimConfig')) : { ...group, claimConfig };
    }
  }
}

/** The switch: on → the child's value as the starting override; off → inherit. */
export function toggleSection(group: GroupDeckung, child: Deckung, section: Section, override: boolean): GroupDeckung {
  if (!override) return clearSection(group, section);
  const start = childValueOf(child, section);
  if (section.kind === 'defaultVariant') return setSection(group, section, child.tariffVariations[0]?.key ?? '');
  return setSection(group, section, structuredClone(start));
}

export type Source = 'TARIF' | 'GROUP_TARIF';

export interface ResolvedDeckung {
  readonly document: Deckung;
  readonly sources: Readonly<Record<string, Source>>;
  readonly defaultVariant: string | undefined;
}

const VARIANT_SECTIONS: readonly Exclude<VariantSection, 'coverageTerm'>[] = ['deductibles', 'approvalLimits', 'waitingPeriod', 'calculationOrder'];

/** The service's merge, locally: allowed variants filter, per-variant sections, two claim-config sections. */
export function resolveGroupDeckung(child: Deckung, group: GroupDeckung | undefined): ResolvedDeckung {
  const sources: Record<string, Source> = { categoryDefinitions: 'TARIF', allowedVariants: 'TARIF' };
  let variations: readonly TariffVariation[] = child.tariffVariations;
  if (group?.allowedVariants !== undefined) {
    const allowed = new Set(group.allowedVariants);
    variations = variations.filter((v) => allowed.has(v.key));
    sources.allowedVariants = 'GROUP_TARIF';
  }
  const merged = variations.map((variation) => {
    const override = group?.variantOverrides?.[variation.key];
    const prefix = `tariffVariations.${variation.key}.`;
    let next: TariffVariation = variation;
    for (const section of VARIANT_SECTIONS) {
      sources[prefix + section] = 'TARIF';
      const value = override?.[section];
      if (value !== undefined) {
        next = { ...next, [section]: value };
        sources[prefix + section] = 'GROUP_TARIF';
      }
    }
    if (override?.coverageTerm !== undefined) {
      next = { ...next, coverageTerm: override.coverageTerm };
      sources[`${prefix}coverageTerm`] = 'GROUP_TARIF';
    }
    const categoryOverrides = override?.categories ?? {};
    if (Object.keys(categoryOverrides).length > 0) {
      const done = new Set<string>();
      const categories = next.categories.map((c) => {
        const replacement = categoryOverrides[c.key];
        if (replacement === undefined) return c;
        done.add(c.key);
        return { ...c, coverageTerm: replacement.coverageTerm };
      });
      for (const key of Object.keys(categoryOverrides).sort()) {
        sources[`${prefix}categories.${key}`] = 'GROUP_TARIF';
        if (!done.has(key)) categories.push({ key, coverageTerm: (categoryOverrides[key] as { coverageTerm: CoverageTerm }).coverageTerm, coverages: [] });
      }
      next = { ...next, categories };
    }
    return next;
  });
  const claimConfig = { ...child.claimConfig };
  for (const section of ['generalSetting', 'claimPositionFields'] as const) {
    sources[`claimConfig.${section}`] = 'TARIF';
    const value = group?.claimConfig?.[section];
    if (value !== undefined) {
      (claimConfig as Record<string, unknown>)[section] = value;
      sources[`claimConfig.${section}`] = 'GROUP_TARIF';
    }
  }
  return { document: { ...child, tariffVariations: merged, claimConfig }, sources, defaultVariant: group?.defaultVariant };
}
